import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Zap, Settings2, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACEHOLDERS = "{first_name} {name} {company} {phone} {book_link}";

export const QuickReplies = ({ phone, disabled, onPick }) => {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const load = useCallback(() => axios.get(`${API}/sms/templates`, { params: { phone } }).then((r) => setTemplates(r.data.templates)).catch(() => {}), [phone]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" title="Quick replies" disabled={disabled} data-testid="sms-quick-replies-button"><Zap className="w-4 h-4 text-amber-500" /></Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2" data-testid="sms-quick-replies-menu">
          <div className="flex items-center justify-between px-2 pb-2 border-b border-slate-100 mb-1">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Quick replies</p>
            <button className="text-xs text-blue-700 hover:underline flex items-center gap-1" onClick={() => { setOpen(false); setManage(true); }} data-testid="sms-quick-replies-manage"><Settings2 className="w-3 h-3" /> Manage</button>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {templates.length === 0 && <p className="text-xs text-slate-400 p-2">No quick replies yet.</p>}
            {templates.map((t) => (
              <button key={t.id} onClick={() => { onPick(t.rendered); setOpen(false); }} className="w-full text-left px-2 py-2 rounded-md hover:bg-slate-50 transition-colors" data-testid="sms-quick-reply-item">
                <p className="text-sm font-medium text-slate-800">{t.title}</p>
                <p className="text-xs text-slate-500 line-clamp-2">{t.rendered}</p>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {manage && <ManageTemplates templates={templates} onClose={() => { setManage(false); load(); }} />}
    </>
  );
};

const TemplateForm = ({ initial, onSave, onCancel }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="sms-template-form">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title, e.g. On my way" data-testid="sms-template-title-input" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Message text" className="w-full rounded-md border border-slate-300 p-2 text-sm" data-testid="sms-template-body-input" />
      <p className="text-[11px] text-slate-500">Placeholders: {PLACEHOLDERS}</p>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="sms-template-cancel">Cancel</Button>
        <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={!title.trim() || !body.trim()} onClick={() => onSave({ title: title.trim(), body: body.trim() })} data-testid="sms-template-save">Save</Button>
      </div>
    </div>
  );
};

const ManageTemplates = ({ templates: initialTemplates, onClose }) => {
  const [templates, setTemplates] = useState(initialTemplates);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const reload = () => axios.get(`${API}/sms/templates`).then((r) => setTemplates(r.data.templates)).catch(() => {});
  const save = async (payload) => {
    try {
      if (editing) await axios.put(`${API}/sms/templates/${editing.id}`, payload); else await axios.post(`${API}/sms/templates`, payload);
      toast.success(editing ? "Quick reply updated" : "Quick reply added");
      setEditing(null); setAdding(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save quick reply"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete quick reply "${t.title}"?`)) return;
    try { await axios.delete(`${API}/sms/templates/${t.id}`); toast.success("Quick reply deleted"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="sms-templates-dialog">
        <DialogHeader><DialogTitle>Manage quick replies</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {templates.map((t) => editing?.id === t.id ? <TemplateForm key={t.id} initial={t} onSave={save} onCancel={() => setEditing(null)} /> : (
            <div key={t.id} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3" data-testid="sms-template-row">
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800">{t.title}</p><p className="text-xs text-slate-500 whitespace-pre-wrap">{t.body}</p></div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600" onClick={() => { setAdding(false); setEditing(t); }} title="Edit" data-testid="sms-template-edit"><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(t)} title="Delete" data-testid="sms-template-delete"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {adding ? <TemplateForm onSave={save} onCancel={() => setAdding(false)} /> : <Button variant="outline" className="w-full" onClick={() => { setEditing(null); setAdding(true); }} data-testid="sms-template-add-button"><Plus className="w-4 h-4 mr-1" /> New quick reply</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
